import { IAuthJwtPayloadDTO, IOrganizationTeamMemberJwtDTO, IProviderTeamMemberJwtDTO } from '../types/user'
import { getAccessJwtPayload } from './auth'

export const Roles = {
  ORGANIZATION_ADMIN: 'ORGANIZATION_ADMIN',
  ORGANIZATION_DISPATCHER: 'ORGANIZATION_DISPATCHER',
  PROVIDER_ADMIN: 'PROVIDER_ADMIN',
  ADMIN: 'ADMIN',
  SUPER_ADMIN: 'SUPER_ADMIN',
  USER: 'USER'
}
export const PERMISSIONS = {
  TRIPS: {
    CREATE: 'TRIPS_CREATE',
    EDIT: 'TRIPS_EDIT',
    VIEW: 'TRIPS_VIEW'
  },
  SCHEDULED_TRIPS: {
    CREATE: 'SCHEDULED_TRIPS_CREATE',
    EDIT: 'SCHEDULED_TRIPS_EDIT',
    VIEW: 'SCHEDULED_TRIPS_VIEW'
  },
  DASHBOARD: {
    VIEW: 'DASHBOARD_VIEW'
  },
  TRIP_HISTORY: {
    VIEW: 'TRIP_HISTORY_VIEW'
  },
  BILLING_ACTIVITY: {
    VIEW: 'BILLING_ACTIVITY_VIEW'
  },
  FEEDBACK: {
    VIEW: 'FEEDBACK_VIEW'
  },
  MESSAGES: {
    VIEW: 'MESSAGES_VIEW'
  },
  GOIN_ZONES: {
    VIEW: 'GOIN_ZONES_VIEW'
  },
  RIDERS: {
    VIEW: 'RIDERS_VIEW'
  },
  MEMBERSHIP_REQUESTS: {
    VIEW: 'MEMBERSHIP_REQUESTS_VIEW'
  },
  ORGANIZATION: {
    VIEW: 'ORGANIZATION_VIEW'
  },
  SERVICES: {
    VIEW: 'SERVICES_VIEW'
  },
  TEAM_MEMBERS: {
    VIEW: 'TEAM_MEMBERS_VIEW'
  },
  PROVIDERS: {
    VIEW: 'PROVIDERS_VIEW'
  },
  DRIVERS: {
    VIEW: 'DRIVERS_VIEW'
  },
  VEHICLES: {
    VIEW: 'VEHICLES_VIEW'
  },
  GLOBAL_SETTINGS: {
    VIEW: 'GLOBAL_SETTINGS_VIEW'
  },
  ADMINS: {
    CREATE: 'ADMINS_CREATE',
    REMOVE: 'ADMINS_REMOVE'
  }
}

export const getObjectValues: (object: object) => string[] = (object: object) =>
  Object.values(object).reduce(
    (acc: string[], cur: string | object) => acc.concat(typeof cur !== 'string' ? getObjectValues(cur) : cur),
    []
  )

export const getAllPermissions = () => getObjectValues(PERMISSIONS)

export const getAllPermissionsExcept = (exclude: string[]) =>
  getAllPermissions().filter((it: string) => !exclude.includes(it))

export const mergePermissions = (...permissions: string[][]) =>
  Array.from(new Set(permissions.reduce((acc, cur) => acc.concat(cur), [])))

const SUPER_ADMIN_PERMISSIONS = getAllPermissions()
const ADMIN_PERMISSIONS = getAllPermissionsExcept([PERMISSIONS.ADMINS.CREATE, PERMISSIONS.ADMINS.REMOVE])
const USER_PERMISSIONS: string[] = []
const ORGANIZATION_ADMIN_PERMISSIONS = [PERMISSIONS.ORGANIZATION.VIEW]
const ORGANIZATION_DISPATCHER_PERMISSIONS = [PERMISSIONS.ORGANIZATION.VIEW]
const PROVIDER_ADMIN_PERMISSIONS = [PERMISSIONS.PROVIDERS.VIEW]

const ROLES_PERMISSIONS = {
  [Roles.SUPER_ADMIN]: SUPER_ADMIN_PERMISSIONS,
  [Roles.ADMIN]: ADMIN_PERMISSIONS,
  [Roles.USER]: USER_PERMISSIONS,
  [Roles.ORGANIZATION_ADMIN]: ORGANIZATION_ADMIN_PERMISSIONS,
  [Roles.ORGANIZATION_DISPATCHER]: ORGANIZATION_DISPATCHER_PERMISSIONS,
  [Roles.PROVIDER_ADMIN]: PROVIDER_ADMIN_PERMISSIONS
}

export const getPermissionsByRoles = (roles: string[]) => mergePermissions(...roles.map(it => ROLES_PERMISSIONS[it]))

const hasPermissions = (roles: string[], requiredPermissions: string[]) => {
  const permissions = getPermissionsByRoles(roles)
  return requiredPermissions.every(it => permissions.includes(it))
}

export interface ICanAccess {
  forOrganization: (organizationId: string, requiredPermissions: string[]) => boolean
  forAnyProvider: (requiredPermissions: string[]) => boolean
  forAnyOrganization: (requiredPermissions: string[]) => boolean
}

export const canAccessForUser = (user: IAuthJwtPayloadDTO): ICanAccess => {
  return {
    forOrganization: (organizationId: string, requiredPermissions: string[]) => {
      const organizationTeamMember = user.rolesInOrganizations.find(it => it.organizationId === organizationId)
      const organizationRoles = (organizationTeamMember && organizationTeamMember.roles.map(it => it.roleName)) || []
      return hasPermissions(user.rolesInApp.concat(organizationRoles), requiredPermissions)
    },
    forAnyProvider: (requiredPermissions: string[]) => {
      const providerRoles = user.rolesInProviders.reduce(
        (acc: string[], cur: IProviderTeamMemberJwtDTO) => acc.concat(cur.roles.map(it => it.roleName)),
        []
      )
      return hasPermissions(user.rolesInApp.concat(providerRoles), requiredPermissions)
    },
    forAnyOrganization: (requiredPermissions: string[]) => {
      const organizationRoles = user.rolesInOrganizations.reduce(
        (acc: string[], cur: IOrganizationTeamMemberJwtDTO) => acc.concat(cur.roles.map(it => it.roleName)),
        []
      )
      return hasPermissions(user.rolesInApp.concat(organizationRoles), requiredPermissions)
    }
  }
}

export const canAccessByAuthToken = () => {
  const user: any = getAccessJwtPayload()
  if (!user) {
    return null
  }
  return canAccessForUser(user)
}

export const checkIfAdmin = () => {
  const accessJwtPayload = getAccessJwtPayload()
  let isAdmin
  if (accessJwtPayload !== null && accessJwtPayload !== undefined) {
    const rolesInOrganizations = accessJwtPayload.rolesInOrganizations.filter(
      org => org.organizationId === localStorage.getItem('currentOrganizationId')
    )[0]

    if (
      rolesInOrganizations !== null &&
      rolesInOrganizations !== undefined &&
      (rolesInOrganizations.roles !== undefined && rolesInOrganizations.roles !== null)
    ) {
      const roleNames = rolesInOrganizations.roles.map(role => role.roleName)

      isAdmin = roleNames.includes('ORGANIZATION_ADMIN')
    } else {
      isAdmin = false
    }
  } else {
    isAdmin = false
  }
  return isAdmin
}



// WEBPACK FOOTER //
// ./src/utils/authorization.ts